import { columnFormatToRecord, parseColumnFormat, type ColumnFormat } from "./columnFormat";
import { headerToColumnKey } from "./tableColumnFormula";

export type TableColumnSource = {
  field: string;
  path: string;
};

export type ColumnBarFill = {
  mode?: "positive" | "negative" | "both";
  color?: string;
  showValue?: boolean;
  min?: number;
  max?: number;
};

export type ColumnValueSource = "field" | "constant" | "formula";

export type TableColumnMapping = {
  header: string;
  field: string;
  path: string;
  sources?: TableColumnSource[];
  format?: ColumnFormat;
  constant?: string | number;
  formula?: string;
  subtotal?: boolean;
  barFill?: ColumnBarFill;
};

export { headerToColumnKey };

export type TableFieldPickerOption = {
  path: string;
  field: string;
  group?: string;
  label: string;
  type?: string;
};

export type TableMapping = {
  rowSource?: string;
  columns: TableColumnMapping[];
};

export function pathToFieldName(path: string): string {
  const parts = path.split(".");
  return parts[parts.length - 1] || path;
}

export function pathToHeader(path: string): string {
  const leaf = pathToFieldName(path);
  return leaf
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function createCustomTableColumn(index: number, header: string): TableColumnMapping {
  return {
    header,
    field: headerToColumnKey(header, index),
    path: "",
    formula: "",
  };
}

export function fieldDescriptorToColumn(path: string, field?: string): TableColumnMapping {
  const normalizedPath = path.trim();
  const normalizedField = (field ?? pathToFieldName(normalizedPath)).trim();
  return {
    header: pathToHeader(normalizedField === pathToFieldName(normalizedPath) ? normalizedPath : normalizedField),
    field: normalizedField,
    path: normalizedPath,
  };
}

/** Row `field` id suggested from aggregation/document group + extraction path. */
export function suggestTableMappingField(group: string | undefined, path: string): string {
  const leaf = pathToFieldName(path);
  const ctx = group?.trim();
  if (!ctx) return leaf;
  if (leaf === "key") return ctx;
  if (leaf === "doc_count") return `${ctx}Count`;
  if (path.includes(".")) return `${ctx}_${leaf}`;
  return leaf;
}

/** Tablo satırındaki hücre anahtarı — mapping.field agg adı, doc_count için suffix. */
export function tableRowFieldKey(aggField: string, path: string, documentRows = false): string {
  const field = aggField.trim();
  const subPath = (path ?? "").trim();
  if (!documentRows) {
    const leaf = pathToFieldName(subPath);
    if (leaf === "doc_count" || leaf === "count") {
      return `${field}Count`;
    }
    return field;
  }
  if (!subPath || subPath === field) {
    return field;
  }
  const suffix = subPath.includes(".") ? pathToFieldName(subPath) : subPath;
  return `${field}${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
}

export function columnHeaderFromAggField(aggField: string, path: string): string {
  const agg = aggField.trim();
  const leaf = pathToFieldName(path);
  if (leaf === "key" || leaf === "value") return pathToHeader(agg);
  if (leaf === "doc_count" || leaf === "count") return `${pathToHeader(agg)} Count`;
  return pathToHeader(`${agg}_${leaf}`);
}

export function columnHeaderFromHitsField(field: string, path: string): string {
  const docField = field.trim();
  const subPath = (path ?? "").trim();
  if (!subPath) {
    return pathToHeader(docField);
  }
  return `${pathToHeader(docField)} ${pathToHeader(subPath)}`;
}

export function buildTableFieldPickerOption(input: {
  path: string;
  group?: string;
  type?: string;
}): TableFieldPickerOption {
  const path = input.path.trim();
  const group = input.group?.trim() || undefined;
  const aggField = group || pathToFieldName(path);
  const pathLeaf = pathToFieldName(path);
  const label = group ? `${group} (${pathLeaf})` : pathLeaf;
  return { path, field: aggField, group, label, type: input.type };
}

export function fieldOptionToColumn(option: Pick<TableFieldPickerOption, "path" | "field" | "group">): TableColumnMapping {
  return fieldOptionsToColumn([option as TableFieldPickerOption]);
}

export function sourceFromFieldOption(option: Pick<TableFieldPickerOption, "path" | "field" | "group">): TableColumnSource {
  const path = (option.path ?? "").trim();
  if (option.group) {
    return { field: option.group.trim(), path };
  }
  return { field: option.field.trim(), path };
}

export function columnHeaderFromSource(source: TableColumnSource, grouped?: boolean): string {
  if (grouped) {
    return columnHeaderFromAggField(source.field, source.path);
  }
  return columnHeaderFromHitsField(source.field, source.path);
}

export function compositeColumnFieldKey(sources: TableColumnSource[], documentRows = true): string {
  return sources
    .map((source) => tableRowFieldKey(source.field, source.path, documentRows))
    .filter(Boolean)
    .join("_");
}

export function fieldOptionsToColumn(
  options: Array<Pick<TableFieldPickerOption, "path" | "field" | "group">>
): TableColumnMapping {
  const normalized = options.map((option) => sourceFromFieldOption(option));
  if (normalized.length === 0) {
    return { header: "", field: "", path: "", sources: undefined };
  }
  if (normalized.length === 1) {
    const source = normalized[0];
    const grouped = Boolean(options[0].group);
    return {
      header: columnHeaderFromSource(source, grouped),
      field: source.field,
      path: source.path,
      sources: undefined,
    };
  }
  const grouped = options.some((option) => Boolean(option.group));
  return {
    header: normalized.map((source) => columnHeaderFromSource(source, grouped)).join(" "),
    field: compositeColumnFieldKey(normalized, !grouped),
    path: "",
    sources: normalized,
  };
}

export function columnUsesCompositeSources(column: Pick<TableColumnMapping, "sources">): boolean {
  return (column.sources?.length ?? 0) > 1;
}

export function columnHasConstant(column: Pick<TableColumnMapping, "constant">): boolean {
  return column.constant !== undefined && column.constant !== null;
}

export function columnHasFormula(column: Pick<TableColumnMapping, "formula">): boolean {
  return column.formula !== undefined;
}

export function columnValueSource(column: Pick<TableColumnMapping, "constant" | "formula">): ColumnValueSource {
  if (columnHasFormula(column)) return "formula";
  if (columnHasConstant(column)) return "constant";
  return "field";
}

export function columnHasDataField(column: Pick<TableColumnMapping, "field" | "path" | "sources" | "constant" | "formula">): boolean {
  if (columnHasConstant(column)) return false;
  if (columnHasFormula(column) && !column.field.trim() && !(column.path ?? "").trim() && !columnUsesCompositeSources(column)) {
    return false;
  }
  if (columnUsesCompositeSources(column)) return true;
  return Boolean(column.field.trim() || (column.path ?? "").trim());
}

export function patchFormulaColumnField(
  column: Pick<TableColumnMapping, "header" | "field">,
  index: number
): string {
  const field = column.field.trim();
  if (field) return field;
  return headerToColumnKey(column.header, index);
}

export const resolveColumnRowKey = patchFormulaColumnField;

function parseBarFill(raw: unknown): ColumnBarFill | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const b = raw as Record<string, unknown>;
  const mode = b.mode != null ? String(b.mode).trim() : undefined;
  const normalizedMode =
    mode === "positive" || mode === "negative" || mode === "both" ? mode : undefined;
  const color = b.color != null ? String(b.color).trim() : undefined;
  const showValue = b.showValue === true || b.showValue === false ? b.showValue : undefined;
  const min = b.min != null && Number.isFinite(Number(b.min)) ? Number(b.min) : undefined;
  const max = b.max != null && Number.isFinite(Number(b.max)) ? Number(b.max) : undefined;
  if (!normalizedMode && !color && showValue === undefined && min === undefined && max === undefined) {
    return undefined;
  }
  return {
    ...(normalizedMode ? { mode: normalizedMode } : {}),
    ...(color ? { color } : {}),
    ...(showValue !== undefined ? { showValue } : {}),
    ...(min !== undefined ? { min } : {}),
    ...(max !== undefined ? { max } : {}),
  };
}

export function columnEffectiveSources(column: Pick<TableColumnMapping, "field" | "path" | "sources">): TableColumnSource[] {
  if (column.sources?.length) {
    return column.sources.map((source) => ({
      field: source.field.trim(),
      path: (source.path ?? "").trim(),
    }));
  }
  return [{ field: column.field.trim(), path: (column.path ?? "").trim() }];
}

export function formatCompositeColumnFieldLabel(
  options: Array<Pick<TableFieldPickerOption, "label" | "group" | "field" | "path">>
): string {
  return options.map((option) => formatTableResultFieldLabel(option)).join(" + ");
}

/** UI label for mapping field pickers — group (pathLeaf), never probe sample values. */
export function formatTableResultFieldLabel(option: {
  label?: string;
  group?: string;
  field?: string;
  path: string;
}): string {
  if (option.label?.trim()) return option.label.trim();
  const pathLeaf = pathToFieldName(option.path);
  const group = option.group?.trim();
  return group ? `${group} (${pathLeaf})` : pathLeaf;
}

export function tableFieldPickerOptionsEqual(
  a: Pick<TableFieldPickerOption, "path" | "field" | "group">,
  b: Pick<TableFieldPickerOption, "path" | "field" | "group">
): boolean {
  if ((a.group || "") !== (b.group || "")) {
    return false;
  }
  if (a.group) {
    return a.path === b.path;
  }
  return a.field === b.field && (a.path ?? "") === (b.path ?? "");
}

export function findTableResultFieldOption<T extends Pick<TableFieldPickerOption, "path" | "field" | "group">>(
  column: Pick<TableColumnMapping, "field" | "path" | "sources">,
  options: T[]
): T | null {
  const matches = findTableResultFieldOptions(column, options);
  return matches.length === 1 ? matches[0] : null;
}

/** Parse free-text field entry such as `category (key)` or `lineItems.name`. */
export function parseCustomFieldPathInput(input: string): TableFieldPickerOption {
  const trimmed = input.trim();
  if (!trimmed) {
    return buildTableFieldPickerOption({ path: "" });
  }
  const parenMatch = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (parenMatch) {
    return buildTableFieldPickerOption({
      path: parenMatch[2].trim(),
      group: parenMatch[1].trim(),
    });
  }
  return buildTableFieldPickerOption({ path: trimmed });
}

export function normalizeFieldPickerSelection(
  selection: Array<TableFieldPickerOption | string>
): TableFieldPickerOption[] {
  return selection
    .map((item) => (typeof item === "string" ? parseCustomFieldPathInput(item) : item))
    .filter((option) => option.path.trim() || option.field.trim());
}

export function resolveColumnFieldPickerValue<T extends TableFieldPickerOption & { type?: string }>(
  column: Pick<TableColumnMapping, "field" | "path" | "sources">,
  options: T[]
): T[] {
  const matched = findTableResultFieldOptions(column, options);
  if (matched.length) {
    return matched;
  }
  return columnEffectiveSources(column)
    .filter((source) => source.field.trim() || source.path.trim())
    .map((source) => {
      const path = (source.path || source.field).trim();
      const field = source.field.trim();
      const pathLeaf = pathToFieldName(path);
      const grouped = Boolean(field && field !== pathLeaf);
      return {
        ...buildTableFieldPickerOption({
          path,
          group: grouped ? field : undefined,
        }),
        type: "custom",
      } as T;
    });
}

export function findTableResultFieldOptions<T extends Pick<TableFieldPickerOption, "path" | "field" | "group">>(
  column: Pick<TableColumnMapping, "field" | "path" | "sources">,
  options: T[]
): T[] {
  if (columnUsesCompositeSources(column)) {
    return column.sources!
      .map((source) =>
        options.find((option) => {
          if (option.group) {
            return option.group === source.field.trim() && option.path === (source.path ?? "").trim();
          }
          return option.field === source.field.trim() && (option.path ?? "") === (source.path ?? "").trim();
        })
      )
      .filter((option): option is T => option != null);
  }
  const path = (column.path ?? "").trim();
  const columnField = column.field.trim();
  if (columnField) {
    const exact = options.find((option) => {
      if (option.group) {
        return option.group === columnField && option.path === path;
      }
      return option.field === columnField && (option.path ?? "") === path;
    });
    if (exact) return [exact];
  }
  if (path) {
    const samePath = options.filter((option) => option.path === path);
    if (samePath.length === 1) return [samePath[0]];
  }
  return [];
}

function parseColumnSources(raw: unknown): TableColumnSource[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const sources: TableColumnSource[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const source = item as Record<string, unknown>;
    const field = String(source.field ?? "").trim();
    if (!field) continue;
    const path = source.path != null ? String(source.path).trim() : "";
    sources.push({ field, path });
  }
  return sources;
}

export function parseTableMapping(raw: unknown): TableMapping {
  if (!raw || typeof raw !== "object") {
    return { columns: [] };
  }
  const m = raw as Record<string, unknown>;
  const columnsRaw = m.columns;
  const columns: TableColumnMapping[] = [];
  if (Array.isArray(columnsRaw)) {
    for (const col of columnsRaw) {
      if (!col || typeof col !== "object") continue;
      const c = col as Record<string, unknown>;
      const field = String(c.field ?? "").trim();
      const path =
        c.path != null
          ? String(c.path).trim()
          : c.source != null
            ? String(c.source).trim()
            : "";
      const sources = parseColumnSources(c.sources);
      const format = parseColumnFormat(c.format);
      const hasConstantKey = Object.prototype.hasOwnProperty.call(c, "constant") && c.constant !== null;
      const constant = hasConstantKey
        ? typeof c.constant === "number" && Number.isFinite(c.constant)
          ? c.constant
          : String(c.constant ?? "")
        : undefined;
      const hasFormulaKey = Object.prototype.hasOwnProperty.call(c, "formula");
      const formula = hasFormulaKey ? String(c.formula ?? "") : undefined;
      const subtotal = c.subtotal === true;
      const barFill = parseBarFill(c.barFill);
      const headerRaw = String(c.header || c.label || "").trim();
      if (!field && sources.length === 0 && !headerRaw && !format && constant === undefined && formula === undefined) {
        continue;
      }
      const normalizedField = field || compositeColumnFieldKey(sources);
      const header =
        headerRaw ||
        (sources.length > 1
          ? sources.map((source) => columnHeaderFromHitsField(source.field, source.path)).join(" ")
          : columnHeaderFromHitsField(normalizedField, path));
      columns.push({
        header,
        field: normalizedField,
        path: sources.length > 1 ? "" : path || sources[0]?.path || "",
        ...(sources.length > 1 ? { sources } : {}),
        ...(format ? { format } : {}),
        ...(constant !== undefined ? { constant } : {}),
        ...(formula !== undefined ? { formula } : {}),
        ...(subtotal ? { subtotal } : {}),
        ...(barFill ? { barFill } : {}),
      });
    }
  }
  const rowSource = m.rowSource != null ? String(m.rowSource) : undefined;
  return { rowSource, columns };
}

export function buildTableMappingJson(mapping: TableMapping): string {
  const body: Record<string, unknown> = {
    columns: mapping.columns.map((c) => {
      const col: Record<string, unknown> = {
        header: c.header,
        field: c.field,
        path: c.path,
      };
      if (columnUsesCompositeSources(c)) {
        col.sources = c.sources;
        col.path = c.path ?? "";
      }
      const format = columnFormatToRecord(c.format);
      if (format) {
        col.format = format;
      }
      if (c.constant !== undefined && c.constant !== null) {
        col.constant = c.constant;
      }
      if (c.formula !== undefined) {
        col.formula = c.formula;
      }
      if (c.subtotal) {
        col.subtotal = true;
      }
      if (c.barFill) {
        col.barFill = c.barFill;
      }
      return col;
    }),
  };
  if (mapping.rowSource) {
    body.rowSource = mapping.rowSource;
  }
  return JSON.stringify(body, null, 2);
}

export function tableMappingToRecord(mapping: TableMapping): Record<string, unknown> {
  return JSON.parse(buildTableMappingJson(mapping)) as Record<string, unknown>;
}
